import { Fragment, useRef } from 'react';
import { breakpoints, Inline, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
import type { SubMenuItem } from '@eti/schema-types';
import { useMediaQuery } from 'usehooks-ts';
import SiteButtonWithDropdownMenu from './SiteButtonWithDropdownMenu';

const unorderedListStyles = css`
  flex-wrap: wrap;
  list-style: none;
`;

const listItemStyles = css`
  position: relative;
`;

const menuItemStyles = css`
  border-radius: 3px;
  color: ${vars.colors.header.content};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 400;

  &:hover {
    background-color: ${vars.colors.header.hover};
  }

  &:focus-visible {
    outline: 2px solid ${vars.colors.header.content};
    outline-offset: 2px;
  }

  @media print {
    display: none;
  }
`;

const buttonStyles = css`
  background-color: transparent;
  border: none;
  padding: 8px;
`;

const linkStyles = css`
  display: block;
  padding: 0;
  text-decoration: none;

  @media (min-width: ${breakpoints._768}) {
    padding: 8px;
  }
`;

interface ListItemProps {
  id: string;
  menu: SubMenuItem[];
  name: string;
}

const DropdownMenuListItem = ({ id, menu, name }: ListItemProps) => {
  const ref = useRef(null);

  return (
    <li className={listItemStyles} ref={ref}>
      <SiteButtonWithDropdownMenu
        className={cx(menuItemStyles, buttonStyles)}
        label={name}
        menu={menu}
        menuId={id}
        wrapperRef={ref}
      />
    </li>
  );
};

interface MenuProps {
  menu: {
    name: string;
    path: string;
    subMenu: SubMenuItem[];
  }[];
}

const DesktopMenu = ({ menu }: MenuProps) => {
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);

  return (
    <Inline as="nav">
      <Inline
        alignY="center"
        as="ul"
        className={unorderedListStyles}
        data-testid="desktop-menu"
        noWrap
        spacing={{ [breakpoints._0]: 32, [breakpoints._768]: 16 }}
      >
        {menu.map(({ name, subMenu, path }, index) => (
          <Fragment key={index}>
            {subMenu?.length > 0 ? (
              <>
                {isWiderViewport && (
                  <DropdownMenuListItem id={`etiID_${index + 1}`} menu={subMenu} name={name} />
                )}
              </>
            ) : (
              <li>
                <a
                  className={cx(menuItemStyles, linkStyles)}
                  data-testid={`menu-link-etiID_${index + 1}`}
                  href={path}
                >
                  {name}
                </a>
              </li>
            )}
          </Fragment>
        ))}
      </Inline>
    </Inline>
  );
};

export default DesktopMenu;
